import React, { useRef } from 'react';
import { useScroll } from 'framer-motion';
import { SHELL_WHITE } from '../../../theme/colors';
import ProtocolMockCards from './ProtocolMockCards';
import HealthProfileChips from './HealthProfileChips';
import SignalGrid from './SignalGrid';
import WeightInterpretationCard from './WeightInterpretationCard';

const OVERLAYS = {
  protocols: ({ progress }) => <ProtocolMockCards progress={progress} />,
  healthProfile: ({ progress }) => <HealthProfileChips progress={progress} />,
  signalGrid: ({ progress }) => <SignalGrid variant="mobile" progress={progress} />,
  weight: ({ progress }) => (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <WeightInterpretationCard progress={progress} />
    </div>
  ),
};

// Extra scroll runway (in vh) each card stays pinned for, dedicated purely
// to the overlay animation, BEFORE the next card's cover transition begins.
// Without this, the animation's 0->1 progress and the "next card slides up
// and covers this one" transition were the same 100vh of scroll — they
// finished at literally the same instant, so the card was already partway
// obscured while its last beats were still playing. Bump this if a card
// still feels rushed; it's a single shared constant, not per-card.
const RUNWAY_VH = 100;

// Each card is an outer (non-sticky, taller-than-viewport) wrapper around an
// inner position:sticky box. The inner box pins at top:0 and — because the
// outer wrapper is RUNWAY_VH taller than it — stays pinned for RUNWAY_VH of
// extra scroll (the standard "long section + pinned child" technique) before
// the next card's own wrapper naturally scrolls up and covers it, exactly as
// before, over a further 100vh. useScroll targets the OUTER wrapper (not the
// sticky child) since a sticky element reports a pinned getBoundingClientRect
// while stuck, which corrupts useScroll's own re-measurement on scroll.
const FeatureCardMobile = ({ feature }) => {
  const Overlay = OVERLAYS[feature.overlay];
  const runwayRef = useRef(null);
  const { scrollYProgress: progress } = useScroll({ target: runwayRef, offset: ['start start', 'end end'] });

  return (
    <div ref={runwayRef} style={{ position: 'relative', width: '100%', height: `calc(100dvh + ${RUNWAY_VH}dvh)` }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          width: '100%',
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: feature.tint
            ? `linear-gradient(rgba(0,0,0,${feature.tint}), rgba(0,0,0,${feature.tint})), url(${feature.image})`
            : `url(${feature.image})`,
          backgroundSize: 'cover',
          backgroundPosition: '50%',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(to bottom, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0) 30%), linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 30%)',
          }}
        />

        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', padding: '56px 24px 0' }}>
          <span style={{ fontFamily: 'Instrument Serif', fontStyle: 'italic', fontSize: 14, color: SHELL_WHITE, opacity: 0.65 }}>
            {feature.number}
          </span>
          <span style={{ fontFamily: 'DM Sans', fontSize: 25, fontWeight: 600, color: SHELL_WHITE, marginTop: 6 }}>{feature.title}</span>
          <span style={{ display: 'block', width: '100%', fontFamily: 'DM Sans', fontSize: 15, lineHeight: 1.5, color: SHELL_WHITE, opacity: 0.85, marginTop: 8 }}>
            {feature.description}
          </span>
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ position: 'relative', width: '100%', boxSizing: 'border-box', padding: '0 20px', marginBottom: feature.overlay ? 60 : 0 }}>
          <Overlay progress={progress} />
        </div>
      </div>
    </div>
  );
};

export default FeatureCardMobile;
